import { DateTime } from 'luxon';
import quickDateTimeFormat from './quickDateTimeFormat';

export enum ScheduleOccurrencesCreationErrorType {
  InvalidEpisodeField,
  InvalidWeeklyRecurrenceWeekDayList,
  InvalidWeeklyRecurrenceClosestWeekDays,
  OccurrenceOutOfBounds
}

export interface IEmptyWeekDayList {
  type: ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceWeekDayList;
}

export enum EpisodeField {
  Every,
  EndDate,
  StartDate
}

/**
 * Occurrence out of bounds error
 */
export interface IOccurrenceOutOfBounds {
  type: ScheduleOccurrencesCreationErrorType.OccurrenceOutOfBounds;
  /**
   * The date of the episode is affected by the overlap
   */
  affects: EpisodeField.EndDate | EpisodeField.StartDate;
  /**
   * Invalid occurrence date
   */
  date: DateTime;
  /**
   * The occurrence date that caused the overlap
   */
  source: EpisodeField.EndDate | EpisodeField.StartDate;
  /**
   * The occurrence id
   */
  occurrenceId: number;
  /**
   * episode error data
   */
  episode: IOccurrenceErrorEpisodeData;
}

export interface IOccurrenceErrorEpisodeData {
  /**
   * The episode start date
   */
  startDate: DateTime;
  /**
   * The episode recurrence end date
   */
  endDate: DateTime;
}

export type ScheduleOccurrencesCreationError =
  | {
      type: ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceClosestWeekDays;
    }
  | IEmptyWeekDayList
  | IOccurrenceOutOfBounds
  | IInvalidEpisodeField;

/**
 * A field in the episode was detected as invalid
 */
export interface IInvalidEpisodeField {
  type: ScheduleOccurrencesCreationErrorType.InvalidEpisodeField;
  field: EpisodeField;
}

/**
 * No closest weekdays found for the weekly recurrence from a specific date
 */
export interface IInvalidWeeklyRecurrenceClosestWeekDays {
  type: ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceClosestWeekDays;
  date: DateTime;
}

function dateTypeToString(
  dateType: EpisodeField.EndDate | EpisodeField.StartDate
) {
  switch (dateType) {
    case EpisodeField.EndDate:
      return 'end';
    case EpisodeField.StartDate:
      return 'start';
  }
}

function simplePluralize(n: number, word: string) {
  return n > 1 ? `${word}s` : word;
}

function formatDifference(a: DateTime, b: DateTime) {
  const differenceUnitList = [
    'weeks',
    'years',
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
    'milliseconds'
  ] as const;
  const diff = a
    .diff(b, Array.from(differenceUnitList))
    .shiftTo(...differenceUnitList);
  const parts = new Array<string>();
  if (diff.years > 0) {
    parts.push(`${diff.years} ${simplePluralize(diff.years, 'year')}`);
  }
  if (diff.months > 0) {
    parts.push(`${diff.months} ${simplePluralize(diff.months, 'month')}`);
  }
  if (diff.weeks > 0) {
    parts.push(`${diff.weeks} ${simplePluralize(diff.weeks, 'week')}`);
  }
  if (diff.days > 0) {
    parts.push(`${diff.days} ${simplePluralize(diff.days, 'day')}`);
  }
  if (diff.hours > 0) {
    parts.push(`${diff.hours} ${simplePluralize(diff.hours, 'hour')}`);
  }
  if (diff.minutes > 0) {
    parts.push(`${diff.minutes} ${simplePluralize(diff.minutes, 'minute')}`);
  }
  if (diff.seconds > 0) {
    parts.push(`${diff.seconds} ${simplePluralize(diff.seconds, 'second')}`);
  }
  if (diff.milliseconds > 0) {
    parts.push(
      `${diff.milliseconds} ${simplePluralize(
        diff.milliseconds,
        'millisecond'
      )}`
    );
  }
  return parts;
}

/**
 * Joins words with commas and "and" at the last item. The "and" is appended to
 * the last item if the list has more than 1 item.
 */
function joinWords(parts: string[]) {
  if (parts.length > 1) {
    const lastPart = parts[parts.length - 1];
    if (typeof lastPart === 'string') {
      parts[parts.length - 1] = `and ${lastPart}`;
    }
  }
  return parts.join(', ');
}

/**
 * Formats a @type {ScheduleOccurrencesCreationError} object to a human-readable string
 * @returns An object with a `message` property, which has a string
 */
export default function formatScheduleOccurrencesCreationError(
  error: ScheduleOccurrencesCreationError
) {
  let message: string;
  switch (error.type) {
    case ScheduleOccurrencesCreationErrorType.InvalidEpisodeField:
      switch (error.field) {
        case EpisodeField.Every:
          message = 'Recurring episode must repeat at least every once.';
          break;
        case EpisodeField.StartDate:
          message = 'Internal: The episode start date is invalid.';
          break;
        case EpisodeField.EndDate:
          message = 'Internal: The episode end date is invalid.';
          break;
      }
      break;
    case ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceWeekDayList:
      message = 'At least one weekday must be selected for weekly recurrences.';
      break;
    case ScheduleOccurrencesCreationErrorType.InvalidWeeklyRecurrenceClosestWeekDays:
      message = 'No closest weekday was found for the weekly recurrence.';
      break;
    case ScheduleOccurrencesCreationErrorType.OccurrenceOutOfBounds: {
      let when: string;
      let affectedDate: DateTime;
      switch (error.affects) {
        case EpisodeField.EndDate:
          affectedDate = error.episode.endDate;
          break;
        case EpisodeField.StartDate:
          affectedDate = error.episode.startDate;
          break;
      }

      let detailedDifference: string;

      if (error.date.toSeconds() > affectedDate.toSeconds()) {
        detailedDifference = joinWords(
          formatDifference(error.date, affectedDate)
        );
        when = 'after';
      } else {
        detailedDifference = joinWords(
          formatDifference(affectedDate, error.date)
        );
        when = 'before';
      }
      const sourceDateLabel = dateTypeToString(error.source);
      const affectedDateLabel = dateTypeToString(error.affects);
      message =
        `The occurrence ${
          error.occurrenceId + 1
        } ${sourceDateLabel}s on ${quickDateTimeFormat(
          error.date
        )}, but is not valid, ` +
        `as it falls ${when} the ${affectedDateLabel} date of the episode: ${quickDateTimeFormat(
          affectedDate
        )}. ` +
        `You might want to readjust the ${sourceDateLabel} date of the recurrence. The difference is ${detailedDifference}.`;
    }
  }
  return {
    message
  };
}
